import React, { useCallback, useRef, useState } from "react";

import { isCaptureDrag, readCaptureDrag } from "./CaptureDragPayload";
import { DropZoneWrap, DropOverlay, DropMessage } from "./CaptureDropZone.styles";

/**
 * The drop TARGET for a capture dragged out of the "Captured items on this
 * wheel" drawer onto the open Segment.
 *
 * WHY THE AFFORDANCES ARE THE FEATURE, not decoration. Kelly's recorded attempt
 * (QA-52 f060–f069) was two drags over ~20 seconds that produced NO drag ghost,
 * NO drop-target highlight and NO rejection message. Nothing on screen changed.
 * Silence reads as broken, and that is precisely why the same thing was
 * reported three times and twice nearly routed to a developer as "never built".
 * So this component's contract is: while a capture is over the Segment, the
 * Segment SAYS what will happen; if the drop cannot be honoured, it SAYS why.
 *
 * `prefers-reduced-motion` is honoured in the styles — an animation that
 * ignores it is a bug wearing a flourish's clothes.
 *
 * Props:
 *   onDropCapture(payload) -> { ok, message } | Promise thereof. Performs the
 *                             insert; the zone only reports the outcome.
 *   isEnabled              flag/ownership gate. OFF renders children verbatim.
 *   canAccept              false when there is nowhere to drop (e.g. no open
 *                          Segment) — the zone then REFUSES visibly.
 *   refusalMessage         what to say when canAccept is false.
 */
const CaptureDropZone = ({
  children,
  onDropCapture,
  isEnabled = false,
  canAccept = true,
  refusalMessage = "Open a Segment first, then drop the capture into it.",
}) => {
  const [isOver, setIsOver] = useState(false);
  const [message, setMessage] = useState(null);
  // dragenter/dragleave fire for every descendant; a depth counter is the only
  // way the highlight does not flicker as the pointer crosses child elements.
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsOver(false);
  }, []);

  if (!isEnabled) return children;

  const handleDragEnter = (e) => {
    if (!isCaptureDrag(e.dataTransfer)) return;
    e.preventDefault();
    depth.current += 1;
    setIsOver(true);
    setMessage(null);
  };

  const handleDragOver = (e) => {
    if (!isCaptureDrag(e.dataTransfer)) return;
    // Without preventDefault the browser refuses the drop and the cursor shows
    // "no entry" — the single most common cause of a drag that "does nothing".
    e.preventDefault();
    // eslint-disable-next-line no-param-reassign
    e.dataTransfer.dropEffect = canAccept ? "copy" : "none";
  };

  const handleDragLeave = (e) => {
    if (!isCaptureDrag(e.dataTransfer)) return;
    depth.current -= 1;
    if (depth.current <= 0) reset();
  };

  const handleDrop = async (e) => {
    if (!isCaptureDrag(e.dataTransfer)) return;
    e.preventDefault();
    reset();

    if (!canAccept) {
      setMessage(refusalMessage);
      return;
    }

    const payload = readCaptureDrag(e.dataTransfer);
    if (!payload) {
      setMessage("That capture couldn't be read. Try the Add to Segment button.");
      return;
    }

    const result = await onDropCapture?.(payload);
    // A refusal from the insert layer is surfaced HERE, on the Segment the user
    // is looking at — never swallowed.
    setMessage(result && result.ok === false ? result.message : null);
  };

  return (
    <DropZoneWrap
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      $active={isOver}
      $accepting={canAccept}
      data-testid="capture-drop-zone"
      data-drag-state={isOver ? (canAccept ? "accepting" : "refusing") : "idle"}
    >
      {children}

      {isOver && (
        <DropOverlay $accepting={canAccept} aria-hidden="true">
          <DropMessage $accepting={canAccept}>
            {canAccept ? "Drop to add to the bottom of this Segment" : refusalMessage}
          </DropMessage>
        </DropOverlay>
      )}

      {/* A rejection AFTER the drop. role=status so a screen-reader user is told
          too, and it is not a colour-only signal. */}
      {message && (
        <DropMessage role="status" $accepting={false} data-testid="capture-drop-message">
          {message}
        </DropMessage>
      )}
    </DropZoneWrap>
  );
};

export default CaptureDropZone;
